
import * as React from 'react';
import { Header } from '@/components/header';
import { Sidebar } from '@/components/sidebar';
import { InspectorPanel } from '@/components/providers';
import type { InspectorTab } from '@/components/providers';
import { EXPAND_PROJECT_EVENT, ProjectModal, SessionsSidebar, planProjectAnswer } from '@/components/sessions';
import { FOCUS_FILES_EVENT } from '@/components/files';
import { Chat, INITIAL_PREFIX } from '@/components/chat';
import { BotsMode } from '@/components/bots/bots-mode';
import { readAppMode, writeAppMode, type AppMode } from '@/components/bots/mode';
import { DesignPage } from '@/components/design';
import { TilingWorkspace, isPaneOnlyTab } from '@/components/panes';
import { LayoutGrid, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { usePaneStore } from '@/stores/pane';
import { DEFAULT_LEFT_WIDTH, DEFAULT_RIGHT_WIDTH } from '@/stores/layout';
import { useWs } from '@/hooks/use-ws';
import { api, type MetricsRes } from '@/lib/api';
import { useInstanceBootstrapped } from '@/lib/bootstrapped';
import { useSessionStore } from '@/stores';
import {
  FooterBar,
  ActivityBar,
  ACTIVITY_MODAL_SECTIONS,
  InspectorRail,
  MobileSingleView,
  OfflineBanner,
  PaneErrorBoundary,
  RAIL_MODAL_SECTIONS,
  SearchModal,
  ShortcutsDialog,
  SHOW_SHORTCUTS_EVENT,
  ToastHost,
  activityInspectorTab,
  activityOpensPaneTab,
  anyDrawerOpen,
  closeAllSidebars,
  emitToast,
  initialSidebarVisibility,
  inspectorRailSide,
  isActivityModalKey,
  isEditableTarget,
  isRailModalTab,
  isRailStandaloneModalTab,
  mobileQuery,
  nextSidebarVisibility,
  readExplorerSide,
  sidebarPanelTitle,
  swappedExplorerSide,
  useIsMobile,
  writeExplorerSide,
  type ActivityKey,
  type ExplorerSide,
  type InspectorRailTab,
  type SidebarSide,
  type SidebarVisibility,
} from '@/components/shell';
import { useFocusTrap } from '@/components/shell/use-focus-trap';
import { browserStorage, settingsDeepLink, writeSettingsFullscreen } from '@/components/settings/settings';
import type { SettingsSectionId } from '@/components/settings/settings';

/**
 * SettingsModal ships as its own chunk (lazy) — the settings dialog loads
 * on first open, never in the initial bundle. The embedded Config /
 * Appearance / Permissions / MCP panes ride that chunk; Providers / Models
 * / Memory / Cron / Plugins keep their own existing pane chunks.
 */
const LazySettingsModal = React.lazy(() =>
  import('@/components/settings/settings-modal').then((m) => ({ default: m.SettingsModal })),
);

/**
 * REQ-167 — the Archify modal ships as its own lazy chunk as well: the
 * rail icon / mobile tools strip load the dialog (and with it the diagram
 * surface) on first open, never in the initial bundle.
 */
const LazyArchifyModal = React.lazy(() =>
  import('@/components/archify/archify-modal').then((m) => ({ default: m.ArchifyModal })),
);

/**
 * AppShell — harness frame: Header + sidebars + chat + footer.
 * Owns the single WS socket (status/cost feed the Header and the offline
 * banner), the session list (sessionStore cache), and the global shortcuts
 * (SHORTCUTS registry: Ctrl/Cmd+K search, Ctrl+M model switch, `?` help,
 * Ctrl+, settings, Esc closes).
 *
 * Responsive (Phase 3 mobile): below the `md` breakpoint both sidebars
 * become exclusive slide-over drawers over a full-width chat — mobile
 * boots with both closed, opening one closes the other, and picking a
 * session dismisses the Explorer drawer.
 *
 * REQ-007: Explorer and Inspector swap physical sides via the header swap
 * button (`explorerSide`, persisted to localStorage) — toggle titles,
 * drawer labels and `[`/`]` shortcut copy all follow the swap.
 *
 * REQ-043: Inspector and Explorer are separate pages — file work lives
 * ONLY behind the rail's Files tab (VS Code Explorer position) where it
 * renders alone, while sessions + the server card stay in the Explorer
 * panel wherever the swap puts it.
 */
export function AppShell({ sessionId }: { sessionId: string }) {
  const [activeId, setActiveId] = React.useState(sessionId);
  const tiling = usePaneStore((s) => s.tiling);
  // REQ-169 — Setup rail entry hides once the instance is bootstrapped (the
  // App boot gate seeds this shared flag from its settings fetch; unknown
  // state stays not-bootstrapped, so the entry is visible on a fresh install).
  const bootstrapped = useInstanceBootstrapped();
  // REQ-024 — mobile is single-view only: the effect below forces these
  // flags off, so a persisted desktop `tiling:true` can never strand a
  // phone viewport inside the pane system.
  const setTiling = usePaneStore((s) => s.setTiling);
  const setWindowed = usePaneStore((s) => s.setWindowed);
  const requestSessionTab = usePaneStore((s) => s.requestSessionTab);
  const requestInspectorTab = usePaneStore((s) => s.requestInspectorTab);
  const requestBrowserOpen = usePaneStore((s) => s.requestBrowserOpen);
  // REQ-019 — resizable sidebars: widths persist in the pane store
  // (`lokma:layout:v1`), the Sidebar handle writes back live via setSideWidth.
  const leftW = usePaneStore((s) => s.leftW);
  const rightW = usePaneStore((s) => s.rightW);
  const setSideWidth = usePaneStore((s) => s.setSideWidth);
  const isMobile = useIsMobile();
  const [sidebars, setSidebars] = React.useState<SidebarVisibility>(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return initialSidebarVisibility(false);
    }
    return initialSidebarVisibility(window.matchMedia(mobileQuery()).matches);
  });
  const [searchOpen, setSearchOpen] = React.useState(false);
  const [shortcutsOpen, setShortcutsOpen] = React.useState(false);
  // REQ-022 — settings open as an OpenCode-style large modal (not a
  // sidebar tab): header gear, activity/rail gear and Ctrl+, all land here.
  const [settingsOpen, setSettingsOpen] = React.useState(false);
  // REQ-072: account rail icon opens the modal on the Account section.
  const [settingsSection, setSettingsSection] = React.useState<SettingsSectionId>('general');
  // REQ-167 — Archify opens as its OWN standalone modal (not a pane, not a
  // Settings section): the rail icon and the mobile tools strip are the
  // launchers and this flag is the single source of truth for the surface.
  const [archifyOpen, setArchifyOpen] = React.useState(false);
  const [serverUp, setServerUp] = React.useState<boolean | null>(null);
  // REQ-018 — status-bar numbers: gateway round-trip, host metrics, stream rate.
  const [latencyMs, setLatencyMs] = React.useState<number | null>(null);
  const [metrics, setMetrics] = React.useState<MetricsRes | null>(null);
  const [tokensPerSec, setTokensPerSec] = React.useState<number | null>(null);
  const rateRef = React.useRef<{ tokens: number; at: number } | null>(null);
  // REQ-007 — which physical side hosts the Explorer (persisted, survives reload).
  const [explorerSide, setExplorerSide] = React.useState<ExplorerSide>(() => readExplorerSide());
  // REQ-161 — top-level surface: the normal chat/workspace mode or the
  // separate Bots section. Persisted, so a reload lands where the user was.
  const [mode, setMode] = React.useState<AppMode>(() => readAppMode());
  const switchMode = React.useCallback((next: AppMode) => {
    setMode(next);
    writeAppMode(next);
  }, []);
  // REQ-010 — the Inspector always lives opposite the Explorer; the thin
  // icon rail docks on its outer edge and follows the REQ-007 swap.
  const inspectorSide: SidebarSide = inspectorRailSide(explorerSide);
  // REQ-008 — activity rail selection + the Inspector tab it requested
  // (null = sessions, which live in the Explorer panel).
  const [activity, setActivity] = React.useState<ActivityKey>('sessions');
  const [inspectorTab, setInspectorTab] = React.useState<InspectorTab | null>(null);

  const swapSides = React.useCallback(() => {
    setExplorerSide((current) => {
      const next = swappedExplorerSide(current);
      writeExplorerSide(next);
      return next;
    });
  }, []);

  // REQ-109 — the browser always opens as a tiling pane tab
  // (session-style: upsert + focus in the last-focused pane, per-agent tabs
  // intact), never in a sidebar. Desktop enables the workspace on demand;
  // mobile has no pane system (REQ-024), so the Inspector drawer stays its
  // browser surface there.
  const openBrowserPane = React.useCallback(() => {
    if (isMobile) {
      setInspectorTab('browser');
      setSidebars((current) => ({ ...current, [inspectorSide]: true }));
      return;
    }
    setTiling(true);
    // REQ-145 — dock the browser as a right-hand pane: a single-pane
    // workspace splits for it, an already-split one reuses its side pane.
    requestInspectorTab('browser', true);
  }, [inspectorSide, isMobile, requestInspectorTab, setTiling]);

  // REQ-008 — rail click reveals the panel that owns the key, then asks
  // the Inspector to switch tabs (prop-driven so a first click that mounts
  // the sidebar still lands on the right tab).
  // REQ-022 — the settings key opens the modal instead (no sidebar change).
  const handleActivitySelect = React.useCallback(
    (key: ActivityKey) => {
      setActivity(key);
      if (key === 'settings') {
        setSettingsSection('general');
        setSettingsOpen(true);
        return;
      }
      if (key === 'account') {
        // REQ-072 + REQ-101: own profile lives in Settings → Account;
        // users/roles/projects/policy live in Settings → Admin.
        setSettingsSection('account');
        setSettingsOpen(true);
        return;
      }
      if (isActivityModalKey(key)) {
        // REQ-165 — Vault left the panes: its icons open Settings → Vault
        // (same section map the rail uses through openSettingsSection).
        setSettingsSection(ACTIVITY_MODAL_SECTIONS[key]);
        setSettingsOpen(true);
        return;
      }
      // REQ-109 — pane-only keys (browser) never touch the sidebar.
      if (activityOpensPaneTab(key)) {
        openBrowserPane();
        return;
      }
      const tab = activityInspectorTab(key);
      setInspectorTab(tab);
      if (tab === null) {
        setSidebars((current) =>
          isMobile
            ? { left: explorerSide === 'left', right: explorerSide === 'right' }
            : { ...current, [explorerSide]: true },
        );
        return;
      }
      setSidebars((current) =>
        isMobile
          ? { left: inspectorSide === 'left', right: inspectorSide === 'right' }
          : { ...current, [inspectorSide]: true },
      );
    },
    [explorerSide, inspectorSide, isMobile, openBrowserPane],
  );

  // REQ-194 Kapsam 3 — deep link: `?settings=models&fullscreen=1` opens the
  // modal straight on that section (optionally full screen). Read once on
  // mount, then the params are stripped so a later reload — and any share
  // of the bare URL — does not re-open a full-height box on top of the
  // user's own surface. `fullscreen=1` lands in the persisted preference,
  // which is the single source the modal reads on open.
  React.useEffect(() => {
    const link = settingsDeepLink(window.location.search);
    if (!link) return;
    setSettingsSection(link.section);
    setSettingsOpen(true);
    if (link.fullscreen) writeSettingsFullscreen(true, browserStorage());
    try {
      const url = new URL(window.location.href);
      url.searchParams.delete('settings');
      url.searchParams.delete('fullscreen');
      window.history.replaceState({}, '', url.toString());
    } catch {
      // A locked-down history API only costs the strip, never the open.
    }
  }, []);

  // REQ-163 — a rail entry that opens a Settings modal section (Agent Hub)
  // instead of a pane/tab. Single callback so every launcher (desktop rail,
  // mobile tools strip) uses the same path.
  const openSettingsSection = React.useCallback((section: SettingsSectionId) => {
    setSettingsSection(section);
    setSettingsOpen(true);
  }, []);

  // REQ-010 — rail click asks the Inspector for the tab (prop-driven, same
  // as REQ-008) and reveals the wide Inspector panel next to the rail.
  // REQ-022 — the settings rail icon opens the modal instead (the Inspector
  // 'settings' tab itself stays reachable for deep-links).
  const handleInspectorRailSelect = React.useCallback(
    (tab: InspectorRailTab) => {
      // REQ-163 — modal entries (Agent Hub) open the Settings modal on their
      // section; they are never tiling panes or sidebar tabs.
      if (isRailModalTab(tab)) {
        openSettingsSection(RAIL_MODAL_SECTIONS[tab]);
        return;
      }
      // REQ-167 — the Archify rail icon opens its standalone modal (the
      // icon stays, the pane/tab definition is gone).
      if (isRailStandaloneModalTab(tab)) {
        setArchifyOpen(true);
        return;
      }
      // REQ-109 — the rail browser entry opens a pane tab, never the sidebar.
      if (isPaneOnlyTab(tab)) {
        openBrowserPane();
        return;
      }
      if (tab === 'settings') {
        setSettingsOpen(true);
        return;
      }
      setInspectorTab(tab);
      setSidebars((current) =>
        isMobile
          ? { left: inspectorSide === 'left', right: inspectorSide === 'right' }
          : { ...current, [inspectorSide]: true },
      );
    },
    [inspectorSide, isMobile, openBrowserPane, openSettingsSection],
  );

  const ws = useWs(activeId);
  const refreshSessions = useSessionStore((s) => s.refreshSessions);
  const selectSession = useSessionStore((s) => s.selectSession);

  // Keep prop + override in sync when the parent session changes.
  React.useEffect(() => {
    setActiveId(sessionId);
  }, [sessionId]);

  // Shrinking a two-panel desktop layout below the mobile breakpoint would
  // stack BOTH drawers over the chat (area C screenshot review). Collapse to
  // a full-width chat instead, matching the fresh mobile boot state.
  React.useEffect(() => {
    if (isMobile) {
      setSidebars((current) =>
        current.left || current.right ? { left: false, right: false } : current,
      );
    }
  }, [isMobile]);

  // REQ-024 — mobile single-view: tiling splits, floating windows and
  // drag-drop targets never open below the breakpoint. Forcing the flags
  // off also heals a persisted desktop `tiling:true` on mobile boot; the
  // entry points (file open, session drag, "open as pane") additionally
  // guard `setTiling(true)` with `isMobileViewport()`.
  React.useEffect(() => {
    if (isMobile) {
      setTiling(false);
      setWindowed(false);
    }
  }, [isMobile, setTiling, setWindowed]);

  // Session list + server liveness (30s poll feeds FooterBar + Header pill).
  // REQ-018 — the same tick also times the health round-trip (gateway
  // latency) and pulls host metrics (cpu/ram/version) best-effort.
  React.useEffect(() => {
    void refreshSessions();
    selectSession(activeId);
    let cancelled = false;
    const checkHealth = (): void => {
      const started = performance.now();
      api
        .health()
        .then(() => {
          if (cancelled) return;
          setServerUp(true);
          setLatencyMs(performance.now() - started);
          api
            .getMetrics()
            .then((res) => {
              if (!cancelled) setMetrics(res);
            })
            .catch(() => {
              // Metrics are best-effort — the footer keeps the last sample.
            });
        })
        .catch(() => {
          if (!cancelled) {
            setServerUp(false);
            setLatencyMs(null);
          }
        });
    };
    checkHealth();
    const timer = setInterval(checkHealth, 30_000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [activeId, refreshSessions, selectSession]);

  // REQ-018 — stream throughput from WS cost deltas: each accumulated-cost
  // change yields an instant rate; 4s without growth falls back to idle.
  const wsCost = ws.cost;
  React.useEffect(() => {
    const tokens = wsCost.inputTokens + wsCost.outputTokens;
    const now = Date.now();
    const prev = rateRef.current;
    rateRef.current = { tokens, at: now };
    if (!prev || now <= prev.at) return;
    const gained = tokens - prev.tokens;
    if (gained <= 0) return;
    setTokensPerSec(gained / ((now - prev.at) / 1000));
    const timer = setTimeout(() => setTokensPerSec(null), 4000);
    return () => clearTimeout(timer);
  }, [wsCost]);

  // REQ-018 — selected project = basename of the active session cwd.
  const sessions = useSessionStore((s) => s.sessions);
  const projectName = React.useMemo(() => {
    const active = sessions.find((s) => s.id === activeId);
    if (!active || !active.cwd) return null;
    const parts = active.cwd.split('/').filter((p) => p.length > 0);
    return parts.length > 0 ? parts[parts.length - 1] : active.cwd;
  }, [sessions, activeId]);

  const toggleSidebar = React.useCallback(
    (side: SidebarSide) => {
      setSidebars((current) => nextSidebarVisibility(current, side, isMobile));
    },
    [isMobile],
  );

  const closeDrawers = React.useCallback(() => {
    setSidebars((current) => closeAllSidebars(current));
  }, []);

  const switchSession = React.useCallback(
    (id: string) => {
      if (!id || id === activeId) return;
      setActiveId(id);
      selectSession(id);
      // REQ-040 — tiling sessions open VS Code-style: the session is
      // appended as a NEW tab in the last-focused pane (existing file/tool
      // tabs stay put, the new tab activates; re-opening the same session
      // just focuses it via upsertSessionTab — never a replace, never a
      // duplicate). Single-chat and mobile keep the plain switch.
      if (!isMobile && tiling) {
        const known = useSessionStore.getState().sessions.find((s) => s.id === id);
        requestSessionTab(id, known?.title || id);
      }
      // On mobile both sidebars are drawers — dismiss them so the chat is
      // visible (REQ-011: files live in the left drawer, sessions in the
      // Explorer drawer, so dismissing one no longer covers both).
      if (isMobile) setSidebars(() => ({ left: false, right: false }));
      void refreshSessions();
    },
    [activeId, isMobile, refreshSessions, selectSession, tiling, requestSessionTab],
  );

  // REQ-057 — agent UI actions: the loop drives the Web UI surface through
  // `ui_action` frames. The terminal opens as a pane tab when tiling
  // (sidebar Inspector tab otherwise); the browser ALWAYS opens as a pane
  // tab (REQ-109 — the workspace is enabled on demand on desktop, the
  // Inspector drawer stays its surface on mobile); a new session opens as
  // a pane tab or switches the single chat, with the agent prompt staged
  // so Chat auto-sends it on socket open. One-shot: every entry is
  // dismissed after handling, toasts keep the action visible in the
  // transcript timeline.
  const uiActions = ws.uiActions;
  const dismissUiAction = ws.dismissUiAction;

  /**
   * REQ-182 — the agent-open project modal. The open_project tool creates
   * the record + session server-side FIRST, then ships the frame and waits
   * for the client's answer; parking the payload in state lets the modal
   * outlive the one-shot queue entry and answer with `project_ack`.
   */
  const [agentProject, setAgentProject] = React.useState<{
    actionId: string;
    projectId: string;
    projectName: string;
    cwd: string;
    targetSessionId: string;
  } | null>(null);
  const sendProjectAck = ws.sendProjectAck;

  const resolveAgentProject = React.useCallback(
    (outcome: 'done' | 'cancelled') => {
      // The decision is pure (project-answer.ts) so the cancel path is unit-
      // tested: 'cancelled' only acks; 'done' also reveals + switches.
      const plan = planProjectAnswer(agentProject, outcome);
      // Idempotent: Open and a queued dismiss can both land — the first wins.
      if (!plan) return;
      setAgentProject(null);
      sendProjectAck(plan.actionId, plan.outcome);
      // The record and its session already exist server-side — completing
      // the move is this: open the project's group in the tree, then land
      // the user in the fresh session (REQ-145 immediacy, no extra clicks).
      if (plan.expandProjectId) {
        window.dispatchEvent(
          new CustomEvent(EXPAND_PROJECT_EVENT, { detail: { projectId: plan.expandProjectId } }),
        );
      }
      if (plan.switchSessionId) switchSession(plan.switchSessionId);
    },
    [agentProject, sendProjectAck, switchSession],
  );

  // REQ-182: the run can end while the modal waits (stop pressed, ack timed
  // out) — close it with the run so a dead gate never holds the screen. No
  // ack is sent: the gate is gone, a late answer would only be logged.
  React.useEffect(() => {
    if (agentProject && ws.done) setAgentProject(null);
  }, [agentProject, ws.done]);

  React.useEffect(() => {
    if (uiActions.length === 0) return;
    for (const entry of uiActions) {
      if (entry.action === 'open_browser') {
        // REQ-146 — the tool already reused/opened the session's tab server
        // side; the signal lets the open BrowserPane refresh to that URL
        // (same tab record) instead of showing a stale page.
        requestBrowserOpen({
          tabId: entry.tabId ?? '',
          url: entry.url ?? '',
          sessionId: entry.sessionId,
          // REQ-193 slice 10 (Kapsam 5) — forward the agent-open stamp so
          // the pane can label a page the agent chose on its first paint.
          openedByAgentAt: entry.openedByAgentAt ?? null,
        });
        openBrowserPane();
        emitToast(`Agent opened browser: ${entry.url ?? ''}`);
      } else if (entry.action === 'open_terminal') {
        if (!isMobile && tiling) {
          requestInspectorTab('terminal');
        } else {
          setInspectorTab('terminal');
          setSidebars((current) => ({ ...current, [inspectorSide]: true }));
        }
        emitToast('Agent opened a terminal');
      } else if (entry.action === 'open_session' && entry.targetSessionId) {
        const id = entry.targetSessionId;
        if (entry.prompt) {
          try {
            sessionStorage.setItem(`${INITIAL_PREFIX}${id}`, entry.prompt);
          } catch {
            // Fresh session still opens; the prompt is dropped only for this tab.
          }
        }
        void refreshSessions();
        if (!isMobile && tiling) {
          requestSessionTab(id, id);
        } else {
          setActiveId(id);
          selectSession(id);
        }
        emitToast(entry.prompt ? 'Agent opened a session with a prompt' : 'Agent opened a new session');
      } else if (entry.action === 'send_to_session' && entry.targetSessionId) {
        // REQ-147: the server already appended the message to the target
        // session and queued its run (a client-side send would double it);
        // this frame only focuses the matching pane so the user watches it
        // arrive.
        const id = entry.targetSessionId;
        void refreshSessions();
        if (!isMobile && tiling) {
          requestSessionTab(id, id);
        } else {
          setActiveId(id);
          selectSession(id);
        }
        emitToast('Agent sent a message to another session');
      } else if (entry.action === 'open_project' && entry.projectId && entry.targetSessionId) {
        // REQ-182: the record already exists — park the frame, open the
        // confirmation modal, and let its answer resolve the agent's wait.
        const cwd = entry.cwd ?? '';
        const projectName = entry.projectName || cwd.split('/').filter(Boolean).pop() || 'project';
        setAgentProject({
          actionId: entry.actionId,
          projectId: entry.projectId,
          projectName,
          cwd,
          targetSessionId: entry.targetSessionId,
        });
        emitToast(`Agent opened project "${projectName}"${cwd ? ` at ${cwd}` : ''}`);
      } else if (entry.action === 'open_loop') {
        // REQ-201 — the agent created a loop. The Loops console (REQ-202) is
        // the surface that lists them; until it ships, the honest minimum is a
        // toast that names the loop, so the user learns a background loop now
        // exists instead of finding out from its token bill.
        emitToast(`Agent created loop "${entry.loopName ?? ''}"${entry.loopId ? ` (${entry.loopId})` : ''}`);
      }
      dismissUiAction(entry.actionId);
    }
  }, [uiActions, dismissUiAction, isMobile, tiling, inspectorSide, openBrowserPane, requestBrowserOpen, requestInspectorTab, requestSessionTab, refreshSessions, selectSession]);

  // REQ-182 — mounted in every mode: the frame can arrive while the user is
  // in Bots or Design, and the confirmation must not hide behind chat.
  const agentProjectModal = agentProject ? (
    <ProjectModal
      open
      mode="agent-open"
      agentValue={{ name: agentProject.projectName, cwd: agentProject.cwd }}
      onClose={() => resolveAgentProject('cancelled')}
      onResolve={resolveAgentProject}
    />
  ) : null;

  // Global shortcuts — every combo is listed in the SHORTCUTS registry so
  // the help dialog (`?`) can never drift from what the keys actually do.
  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const mod = e.ctrlKey || e.metaKey;
      if (mod && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        setSearchOpen((v) => !v);
        return;
      }
      if (mod && (e.key === 'm' || e.key === 'M')) {
        e.preventDefault();
        // REQ-012 — the header model select is gone; Ctrl+M opens the
        // Composer's own model popup (it toggles on click).
        document.getElementById('lokma-composer-model')?.click();
        return;
      }
      if (mod && (e.key === 'p' || e.key === 'P')) {
        e.preventDefault();
        // REQ-011 — files live fixed on the left, so reveal the left panel.
        setSidebars((current) => nextSidebarVisibility(current, 'left', isMobile));
        window.dispatchEvent(new Event(FOCUS_FILES_EVENT));
        return;
      }
      if (mod && e.key === ',') {
        e.preventDefault();
        // REQ-022 — Ctrl/Cmd+, opens the settings modal (`?` stays help).
        setSettingsOpen(true);
        return;
      }
      if (e.key === 'Escape') {
        setSearchOpen(false);
        setShortcutsOpen(false);
        // Dismiss open drawers first so one keypress always reveals the chat.
        setSidebars((current) => closeAllSidebars(current));
        return;
      }
      if (isEditableTarget(e.target)) {
        return;
      }
      if (e.key === '[') toggleSidebar('left');
      if (e.key === ']') toggleSidebar('right');
      if (e.key === '?') {
        e.preventDefault();
        setShortcutsOpen(true);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isMobile, toggleSidebar]);

  // Footer hint + panes open the dialog through this event (no prop drilling).
  React.useEffect(() => {
    const open = () => setShortcutsOpen(true);
    window.addEventListener(SHOW_SHORTCUTS_EVENT, open);
    return () => window.removeEventListener(SHOW_SHORTCUTS_EVENT, open);
  }, []);

  // REQ-195 — the Explorer panel is the session list ONLY; the server card that
  // used to sit under it is gone (same call as REQ-175 on the header badge).
  // Reachability stays visible in the footer (`gateway · Nms` / `gateway down`)
  // and Settings → General keeps its server line, so nothing is lost.
  const explorerContent = <SessionsSidebar activeId={activeId} onSelect={switchSession} />;

  // REQ-043 — Inspector and Explorer are SEPARATE pages now (supersedes the
  // REQ-034 docking): the FileBrowser lives ONLY behind the rail's Files
  // tab (VS Code Explorer position, first icon) where it renders alone —
  // no sessions/server card on that page. Other Inspector tabs render
  // without files, so each page shows exactly its own content. Session
  // scope is unchanged (`key` remounts per session, exactly as before).
  const inspectorContent = (
    <div className="space-y-4">
      <InspectorPanel
        onOpenSession={switchSession}
        sessionId={activeId}
        ws={ws}
        requestedTab={inspectorTab}
        browserMode={isMobile ? 'inline' : 'pane-redirect'}
      />
    </div>
  );

  // REQ-007 — panel content follows the swap; titles/labels never hardcode sides.
  const leftPanel = sidebarPanelTitle('left', explorerSide);
  const rightPanel = sidebarPanelTitle('right', explorerSide);
  const leftContent = explorerSide === 'left' ? explorerContent : inspectorContent;
  const rightContent = explorerSide === 'left' ? inspectorContent : explorerContent;

  // REQ-161 — the Bots section is its own surface: header + bot list + the
  // selected bot's chat, no rails/sidebars/tiling. The normal mode's pane
  // layout lives in the persisted stores, so switching back to `lokma`
  // restores it untouched (and the remembered bot comes back with it).
  if (mode === 'bots') {
    return (
      <div className="flex h-screen flex-col bg-background text-foreground">
        <a
          href="#lokma-chat"
          className="sr-only focus:not-sr-only focus:absolute focus:top-1 focus:left-1 focus:z-[70] focus:rounded-md focus:bg-[#262624] focus:px-3 focus:py-1.5 focus:text-xs focus:text-white"
        >
          Skip to chat
        </a>
        <Header
          cost={ws.cost}
          wsStatus={ws.status}
          onSearch={() => setSearchOpen(true)}
          onOpenSettings={() => setSettingsOpen(true)}
          onToggleLeft={() => toggleSidebar('left')}
          onToggleRight={() => toggleSidebar('right')}
          explorerSide={explorerSide}
          mode={mode}
          onModeChange={switchMode}
          hideSideToggles
        />
        <OfflineBanner status={ws.status} onRetry={ws.reconnect} />
        <div id="lokma-chat" className="flex min-h-0 flex-1 flex-col overflow-hidden">
          <PaneErrorBoundary paneName="Bots">
            <BotsMode
              onOpenSession={(id) => {
                switchSession(id);
                switchMode('chat');
              }}
            />
          </PaneErrorBoundary>
        </div>
        <FooterBar
          serverUp={serverUp}
          latencyMs={latencyMs}
          projectName={projectName}
          cpuPercent={metrics?.cpuPercent ?? null}
          memUsedBytes={metrics?.memory.usedBytes ?? null}
          memTotalBytes={metrics?.memory.totalBytes ?? null}
          tokensPerSec={tokensPerSec}
          version={metrics?.version ?? null}
        />
        <SearchModal
          open={searchOpen}
          onClose={() => setSearchOpen(false)}
          onSelectSession={(id) => {
            switchSession(id);
            switchMode('chat');
          }}
        />
        <ShortcutsDialog open={shortcutsOpen} onClose={() => setShortcutsOpen(false)} explorerSide={explorerSide} />
        {settingsOpen ? (
          <React.Suspense fallback={null}>
            <LazySettingsModal
              open={settingsOpen}
              onClose={() => setSettingsOpen(false)}
              explorerSide={explorerSide}
              initialSection={settingsSection}
            />
          </React.Suspense>
        ) : null}
        {/* REQ-167 — the standalone Archify modal rides its own lazy chunk. */}
        {archifyOpen ? (
          <React.Suspense fallback={null}>
            <LazyArchifyModal open={archifyOpen} onClose={() => setArchifyOpen(false)} />
          </React.Suspense>
        ) : null}
        {agentProjectModal}
        <ToastHost />
      </div>
    );
  }

  // REQ-168 — the Design Studio is its own PAGE: the top switch swaps the
  // whole workspace for it (Bots-mode pattern) — no rails, no sidebars and
  // no panes; `lokma` returns to the untouched normal mode. The page keeps
  // its own state (selected artifact + brief form) in a localStorage
  // snapshot, so re-opening it lands where the user left off.
  if (mode === 'design') {
    return (
      <div className="flex h-screen flex-col bg-background text-foreground">
        <a
          href="#lokma-chat"
          className="sr-only focus:not-sr-only focus:absolute focus:top-1 focus:left-1 focus:z-[70] focus:rounded-md focus:bg-[#262624] focus:px-3 focus:py-1.5 focus:text-xs focus:text-white"
        >
          Skip to design studio
        </a>
        <Header
          cost={ws.cost}
          wsStatus={ws.status}
          onSearch={() => setSearchOpen(true)}
          onOpenSettings={() => setSettingsOpen(true)}
          onToggleLeft={() => toggleSidebar('left')}
          onToggleRight={() => toggleSidebar('right')}
          explorerSide={explorerSide}
          mode={mode}
          onModeChange={switchMode}
          hideSideToggles
        />
        <OfflineBanner status={ws.status} onRetry={ws.reconnect} />
        <div id="lokma-chat" className="flex min-h-0 flex-1 flex-col overflow-hidden">
          <PaneErrorBoundary paneName="Design">
            <DesignPage />
          </PaneErrorBoundary>
        </div>
        <FooterBar
          serverUp={serverUp}
          latencyMs={latencyMs}
          projectName={projectName}
          cpuPercent={metrics?.cpuPercent ?? null}
          memUsedBytes={metrics?.memory.usedBytes ?? null}
          memTotalBytes={metrics?.memory.totalBytes ?? null}
          tokensPerSec={tokensPerSec}
          version={metrics?.version ?? null}
        />
        <SearchModal
          open={searchOpen}
          onClose={() => setSearchOpen(false)}
          onSelectSession={(id) => {
            switchSession(id);
            switchMode('chat');
          }}
        />
        <ShortcutsDialog open={shortcutsOpen} onClose={() => setShortcutsOpen(false)} explorerSide={explorerSide} />
        {settingsOpen ? (
          <React.Suspense fallback={null}>
            <LazySettingsModal
              open={settingsOpen}
              onClose={() => setSettingsOpen(false)}
              explorerSide={explorerSide}
              initialSection={settingsSection}
            />
          </React.Suspense>
        ) : null}
        {/* REQ-167 — the standalone Archify modal rides its own lazy chunk. */}
        {archifyOpen ? (
          <React.Suspense fallback={null}>
            <LazyArchifyModal open={archifyOpen} onClose={() => setArchifyOpen(false)} />
          </React.Suspense>
        ) : null}
        {agentProjectModal}
        <ToastHost />
      </div>
    );
  }

  // REQ-024 — mobile single-view branch: below the breakpoint the harness
  // renders a separate simple mode (one surface + bottom tab bar) instead
  // of the desktop frame. No activity/inspector rails, no tiling toggle or
  // workspace — every feature lives in the tabbed single surface,
  // settings/search stay header modals as on desktop.
  // REQ-091 — the header keeps the left/right sidebar toggles: they open
  // the Explorer/Inspector panels as exclusive slide-over drawers (same
  // `toggleSidebar` + `MobileDrawer` as desktop, swap-aware titles). The
  // swap button stays hidden (no `onSwapSides`) — there is no room for it.
  if (isMobile) {
    return (
      <div className="flex h-screen flex-col bg-background text-foreground">
        <a
          href="#lokma-chat"
          className="sr-only focus:not-sr-only focus:absolute focus:top-1 focus:left-1 focus:z-[70] focus:rounded-md focus:bg-[#262624] focus:px-3 focus:py-1.5 focus:text-xs focus:text-white"
        >
          Skip to chat
        </a>
        <Header
          cost={ws.cost}
          wsStatus={ws.status}
          onSearch={() => setSearchOpen(true)}
          onOpenSettings={() => setSettingsOpen(true)}
          onToggleLeft={() => toggleSidebar('left')}
          onToggleRight={() => toggleSidebar('right')}
          explorerSide={explorerSide}
          mode={mode}
          onModeChange={switchMode}
        />
        <OfflineBanner status={ws.status} onRetry={ws.reconnect} />
        <div id="lokma-chat" className="flex min-h-0 flex-1 flex-col overflow-hidden">
          <PaneErrorBoundary paneName="Mobile">
            <MobileSingleView
              activeId={activeId}
              ws={ws}
              onSelectSession={switchSession}
              requestedTab={inspectorTab}
              onOpenSettingsSection={openSettingsSection}
              onOpenArchifyModal={() => setArchifyOpen(true)}
            />
          </PaneErrorBoundary>
        </div>
        {sidebars.left ? (
          <MobileDrawer side="left" label={`${leftPanel} panel`} onClose={closeDrawers}>
            <PaneErrorBoundary paneName={leftPanel}>
              <Sidebar side="left" title={leftPanel} hideHeader className="h-full w-full">
                {leftContent}
              </Sidebar>
            </PaneErrorBoundary>
          </MobileDrawer>
        ) : null}
        {sidebars.right ? (
          <MobileDrawer side="right" label={`${rightPanel} panel`} onClose={closeDrawers}>
            <PaneErrorBoundary paneName={rightPanel}>
              <Sidebar side="right" title={rightPanel} hideHeader className="h-full w-full">
                {rightContent}
              </Sidebar>
            </PaneErrorBoundary>
          </MobileDrawer>
        ) : null}
        <FooterBar
          serverUp={serverUp}
          latencyMs={latencyMs}
          projectName={projectName}
          cpuPercent={metrics?.cpuPercent ?? null}
          memUsedBytes={metrics?.memory.usedBytes ?? null}
          memTotalBytes={metrics?.memory.totalBytes ?? null}
          tokensPerSec={tokensPerSec}
          version={metrics?.version ?? null}
        />
        <SearchModal
          open={searchOpen}
          onClose={() => setSearchOpen(false)}
          onSelectSession={(id) => {
            switchSession(id);
            emitToast(`Switched to ${id.slice(0, 24)}`);
          }}
        />
        <ShortcutsDialog open={shortcutsOpen} onClose={() => setShortcutsOpen(false)} explorerSide={explorerSide} />
        {settingsOpen ? (
          <React.Suspense fallback={null}>
            <LazySettingsModal
              open={settingsOpen}
              onClose={() => setSettingsOpen(false)}
              explorerSide={explorerSide}
              initialSection={settingsSection}
            />
          </React.Suspense>
        ) : null}
        {/* REQ-167 — the standalone Archify modal rides its own lazy chunk. */}
        {archifyOpen ? (
          <React.Suspense fallback={null}>
            <LazyArchifyModal open={archifyOpen} onClose={() => setArchifyOpen(false)} />
          </React.Suspense>
        ) : null}
        {agentProjectModal}
        <ToastHost />
      </div>
    );
  }

  return (
    <div className="flex h-screen flex-col bg-background text-foreground">
      <a
        href="#lokma-chat"
        className="sr-only focus:not-sr-only focus:absolute focus:top-1 focus:left-1 focus:z-[70] focus:rounded-md focus:bg-[#262624] focus:px-3 focus:py-1.5 focus:text-xs focus:text-white"
      >
        Skip to chat
      </a>
      <Header
        cost={ws.cost}
        wsStatus={ws.status}
        onSearch={() => setSearchOpen(true)}
        onOpenSettings={() => setSettingsOpen(true)}
        onToggleLeft={() => toggleSidebar('left')}
        onToggleRight={() => toggleSidebar('right')}
        explorerSide={explorerSide}
        onSwapSides={swapSides}
        mode={mode}
        onModeChange={switchMode}
      />
      <OfflineBanner status={ws.status} onRetry={ws.reconnect} />
      <div className="flex flex-1 overflow-hidden">
        {/* REQ-021 — rails travel WITH their panels across the REQ-007 swap:
            the left outer edge hosts the Explorer's ActivityBar when the
            Explorer is left, otherwise the Inspector's InspectorRail (and
            mirrored on the right) — no rail is ever stranded next to the
            other panel. */}
        {explorerSide === 'left' ? (
          <ActivityBar active={activity} onSelect={handleActivitySelect} side="left" />
        ) : (
          <InspectorRail active={inspectorTab ?? 'files'} onSelect={handleInspectorRailSelect} side="left" bootstrapped={bootstrapped} />
        )}
        {sidebars.left ? (
          isMobile ? (
            <MobileDrawer side="left" label={`${leftPanel} panel`} onClose={closeDrawers}>
              <PaneErrorBoundary paneName={leftPanel}>
                <Sidebar side="left" title={leftPanel} hideHeader className="h-full w-full">
                  {leftContent}
                </Sidebar>
              </PaneErrorBoundary>
            </MobileDrawer>
          ) : (
            <PaneErrorBoundary paneName={leftPanel}>
              <Sidebar
                side="left"
                title={leftPanel}
                hideHeader
                width={leftW}
                defaultWidth={DEFAULT_LEFT_WIDTH}
                onResize={(w) => setSideWidth('left', w)}
              >
                {leftContent}
              </Sidebar>
            </PaneErrorBoundary>
          )
        ) : null}

        <main id="lokma-chat" tabIndex={-1} className="flex min-w-0 flex-1 flex-col overflow-hidden p-2 outline-none sm:p-3">
          <TilingToggle />
          <PaneErrorBoundary paneName="Chat">
            {tiling ? (
              <TilingWorkspace sessionId={activeId} ws={ws} onOpenSession={switchSession} />
            ) : (
              <Chat key={activeId} sessionId={activeId} ws={ws} onOpenSession={switchSession} />
            )}
          </PaneErrorBoundary>
        </main>

        {sidebars.right ? (
          isMobile ? (
            <MobileDrawer side="right" label={`${rightPanel} panel`} onClose={closeDrawers}>
              <PaneErrorBoundary paneName={rightPanel}>
                <Sidebar side="right" title={rightPanel} hideHeader className="h-full w-full">
                  {rightContent}
                </Sidebar>
              </PaneErrorBoundary>
            </MobileDrawer>
          ) : (
            <PaneErrorBoundary paneName={rightPanel}>
              <Sidebar
                side="right"
                title={rightPanel}
                hideHeader
                width={rightW}
                defaultWidth={DEFAULT_RIGHT_WIDTH}
                onResize={(w) => setSideWidth('right', w)}
              >
                {rightContent}
              </Sidebar>
            </PaneErrorBoundary>
          )
        ) : null}
        {/* REQ-021 — right outer edge mirrors the left: the Inspector rail
            when the Inspector is right, otherwise the Explorer's activity
            bar (REQ-008 rail now travels with its panel instead of staying
            pinned far right). */}
        {explorerSide === 'left' ? (
          <InspectorRail active={inspectorTab ?? 'files'} onSelect={handleInspectorRailSelect} side="right" bootstrapped={bootstrapped} />
        ) : (
          <ActivityBar active={activity} onSelect={handleActivitySelect} side="right" />
        )}
      </div>
      <FooterBar
        serverUp={serverUp}
        latencyMs={latencyMs}
        projectName={projectName}
        cpuPercent={metrics?.cpuPercent ?? null}
        memUsedBytes={metrics?.memory.usedBytes ?? null}
        memTotalBytes={metrics?.memory.totalBytes ?? null}
        tokensPerSec={tokensPerSec}
        version={metrics?.version ?? null}
      />
      <SearchModal
        open={searchOpen}
        onClose={() => setSearchOpen(false)}
        onSelectSession={(id) => {
          switchSession(id);
          emitToast(`Switched to ${id.slice(0, 24)}`);
        }}
      />
      <ShortcutsDialog open={shortcutsOpen} onClose={() => setShortcutsOpen(false)} explorerSide={explorerSide} />
      {settingsOpen ? (
        <React.Suspense fallback={null}>
          <LazySettingsModal
            open={settingsOpen}
            onClose={() => setSettingsOpen(false)}
            explorerSide={explorerSide}
            initialSection={settingsSection}
          />
        </React.Suspense>
      ) : null}
      {/* REQ-167 — the standalone Archify modal rides its own lazy chunk. */}
      {archifyOpen ? (
        <React.Suspense fallback={null}>
          <LazyArchifyModal open={archifyOpen} onClose={() => setArchifyOpen(false)} />
        </React.Suspense>
      ) : null}
      {agentProjectModal}
      <ToastHost />
    </div>
  );
}

// MobileDrawer — slide-over shell for narrow viewports: a backdrop plus a
// fluid-width panel (`85vw`, capped at 320px) docked to one edge. Only
// rendered below the `md` breakpoint (`md:hidden`); desktop keeps the
// static side-by-side sidebars. Locks body scroll while open; the global
// Escape handler and the backdrop both dismiss it.
function MobileDrawer({
  side,
  label,
  onClose,
  children,
}: {
  side: SidebarSide;
  label: string;
  onClose: () => void;
  children: React.ReactNode;
}) {
  React.useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);
  // Mounted only while the drawer is open — trap for the whole lifetime so
  // Tab cannot reach the chat behind the overlay (shared `useFocusTrap`).
  const panelRef = React.useRef<HTMLDivElement>(null);
  useFocusTrap(true, panelRef, { onEscape: onClose });

  return (
    <div className="fixed inset-0 z-50 md:hidden" role="dialog" aria-modal="true" aria-label={label}>
      <button
        type="button"
        aria-label={`Close ${label}`}
        onClick={onClose}
        className="absolute inset-0 h-full w-full cursor-default bg-black/40"
      />
      <div
        ref={panelRef}
        className={`absolute inset-y-0 h-full w-[85vw] max-w-[320px] bg-card shadow-2xl ${
          side === 'left' ? 'left-0' : 'right-0'
        }`}
      >
        <button
          type="button"
          onClick={onClose}
          title={`Close ${label}`}
          aria-label={`Close ${label}`}
          autoFocus
          className="absolute right-2 top-2 z-10 grid h-7 w-7 place-items-center rounded-md text-zinc-500 hover:bg-[#F2F0EB]"
        >
          <X className="h-3.5 w-3.5" />
        </button>
        <div className="h-full overflow-hidden">{children}</div>
      </div>
    </div>
  );
}

// TilingToggle — enters the W7 tiling workspace (split/windowed panes over
// live sessions and Inspector tools); layout and tabs restore from the
// persisted stores. REQ-045 removed the TilingBar strip; REQ-090 removed the
// compact mode cluster too (Single/Windowed/Reset) — no mode buttons remain
// while tiling. REQ-024 — desktop-only: the mobile single-view never offers
// the pane system, so the toggle stays hidden below the breakpoint.
function TilingToggle() {
  const tiling = usePaneStore((s) => s.tiling);
  const setTiling = usePaneStore((s) => s.setTiling);
  const isMobile = useIsMobile();
  if (isMobile) return null;
  if (!tiling) {
    return (
      <div className="mb-2 flex shrink-0 justify-end">
        <Button
          variant="outline"
          size="sm"
          className="h-7 gap-1.5 text-xs"
          title="Open the tiling workspace (split panes, floating windows, drag sessions and files)"
          onClick={() => setTiling(true)}
        >
          <LayoutGrid className="h-3 w-3" />
          Tiling workspace
        </Button>
      </div>
    );
  }
  // REQ-090: no mode buttons while tiling — the cluster is gone.
  return null;
}
